import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE } from "../i18n/locale";
import { Icon, type IconName } from "../components/icons";
import { Alert, PageHeader, Skeleton, buttonStyles, cx } from "../components/ui";
import {
  AFFILIATE_API_URL,
  AFFILIATE_URL,
  commissionFor,
  formatCommissionRate,
  formatTermsAmount,
  type AffiliateProgram,
} from "../lib/affiliate";
import { useAffiliateProgram } from "../lib/queries";
import { SITE_URL } from "../lib/site";

/**
 * The affiliate programme, told to the people most likely to recommend
 * Maxlayer: the ones already using it.
 *
 * Every figure comes from `GET /v1/affiliate/program`, the row an operator
 * edits, and none is written here. When that read is not available the page
 * still offers the programme, and everything that would need a figure — the
 * numbers strip, the estimate, the amounts in the answers — is left out rather
 * than guessed.
 *
 * The cap is never more than a line away from the rate. Half of a first top-up
 * reads as uncapped on its own, and somebody promoting the programme under that
 * impression is where the complaint would start — which is also why the
 * estimate says so the moment the cap is what decides the number.
 */
export function ReferralsPage() {
  const { t } = useTranslation("referrals");
  const { data: program, isPending } = useAffiliateProgram(Boolean(AFFILIATE_API_URL));
  const loading = Boolean(AFFILIATE_API_URL) && isPending;
  const closed = program?.acceptingReferrals === false;

  if (!AFFILIATE_URL) return null;

  const cta = closed ? t("ctaClosed") : t("ctaOpen");

  return (
    <div className="space-y-10">
      <PageHeader
        title={t("title")}
        description={t("description")}
      />

      {closed && (
        <Alert tone="info">
          {t("closed")}
        </Alert>
      )}

      <section aria-labelledby="referral-hero" className="card relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={GLOW} />

        <div className="relative grid grid-cols-1 gap-10 p-6 sm:p-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:items-center">
          <div className="min-w-0 space-y-6">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-accent/30 bg-accent/8 px-2 py-1 text-[12px] font-medium text-accent">
              <Icon name="gift" className="size-3.5" />
              {t("badge")}
            </span>

            <h2
              id="referral-hero"
              className="text-[1.75rem] leading-[1.12] font-normal tracking-[-0.03em] text-balance text-fg sm:text-[2.125rem]"
            >
              <span className="block">{t("heroTitle")}</span>
              <span className="block text-fg-muted">{t("heroSubtitle")}</span>
            </h2>

            <p className="max-w-md text-sm leading-6 text-fg-muted">
              {program
                ? t("heroLead", {
                    rate: formatCommissionRate(program.commissionBps),
                    cap: formatTermsAmount(program.commissionCapMicros),
                  })
                : t("heroLeadNoProgram")}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <AffiliateLink label={cta} className="w-full sm:w-auto" />
              {program?.termsUrl && (
                <a
                  href={program.termsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={buttonStyles("secondary", "lg", "w-full sm:w-auto")}
                >
                  {t("terms")}
                </a>
              )}
            </div>

            <p className="flex items-center gap-2 text-[12px] text-fg-subtle">
              <Icon name="check" className="size-3.5 shrink-0 text-mint" />
              {t("noSignUp")}
            </p>
          </div>

          <LinkPreview program={program} />
        </div>

        {loading ? (
          <div className="relative grid grid-cols-2 gap-px border-t border-line bg-line lg:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="space-y-2.5 bg-surface px-6 py-5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-16" />
              </div>
            ))}
          </div>
        ) : (
          program && (
            <dl className="relative grid grid-cols-2 gap-px border-t border-line bg-line lg:grid-cols-4">
              <Stat
                label={t("stats.youGet")}
                value={formatCommissionRate(program.commissionBps)}
                note={t("stats.youGetNote")}
              />
              <Stat
                label={t("stats.upTo")}
                value={formatTermsAmount(program.commissionCapMicros)}
                note={t("stats.upToNote")}
              />
              <Stat
                label={t("stats.linkLasts")}
                value={String(program.cookieDays)}
                note={t("days", { count: program.cookieDays })}
              />
              <Stat
                label={t("stats.payoutFrom")}
                value={formatTermsAmount(program.minPayoutMicros)}
                note={t("stats.payoutNote")}
              />
            </dl>
          )
        )}
      </section>

      <section aria-labelledby="referral-steps" className="space-y-4">
        <SectionHeading id="referral-steps" title={t("steps.title")} />
        <ol className="grid gap-3 md:grid-cols-3 md:gap-6">
          <Step n={1} icon="link" title={t("steps.share.title")} last={false}>
            {t("steps.share.body")}
          </Step>
          <Step n={2} icon="team" title={t("steps.signUp.title")} last={false}>
            {program
              ? t("steps.signUp.body", {
                  window: t("daysCount", { count: program.cookieDays }),
                })
              : t("steps.signUp.bodyNoProgram")}
          </Step>
          <Step n={3} icon="card" title={t("steps.earn.title")} last>
            {program
              ? t("steps.earn.body", {
                  rate: formatCommissionRate(program.commissionBps),
                  cap: formatTermsAmount(program.commissionCapMicros),
                })
              : t("steps.earn.bodyNoProgram")}
          </Step>
        </ol>
      </section>

      {program && !closed && <Estimate program={program} />}

      <section
        aria-labelledby="referral-faq"
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-10"
      >
        <div className="space-y-2">
          <SectionHeading id="referral-faq" title={t("faq.title")} />
          {program?.termsUrl && (
            <a
              href={program.termsUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            >
              {t("faq.full")}
              <Icon name="arrow-up-right" className="size-3" />
            </a>
          )}
        </div>
        <div className="card divide-y divide-line">
          {answers(program, t).map((item) => (
            <details key={item.question} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[13px] font-medium text-fg transition-colors duration-200 ease-smooth hover:bg-fg/[0.03] pointer-coarse:min-h-11 [&::-webkit-details-marker]:hidden">
                {item.question}
                <Icon
                  name="chevron-down"
                  className="size-4 shrink-0 text-fg-subtle transition-transform duration-200 ease-smooth group-open:rotate-180"
                />
              </summary>
              <p className="-mt-1 px-5 pb-4 text-[13px] leading-6 text-fg-muted">{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="card relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={GLOW_LOW} />
        <div className="relative flex flex-col items-start gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="space-y-1">
            <h2 className="text-lg font-normal tracking-[-0.02em] text-fg">
              {closed ? t("footer.titleClosed") : t("footer.title")}
            </h2>
            <p className="text-[13px] text-fg-muted">
              {closed ? t("footer.leadClosed") : t("footer.lead")}
            </p>
          </div>
          <AffiliateLink label={cta} className="w-full sm:w-auto" />
        </div>
      </section>
    </div>
  );
}

/* The shell's own wash, turned up for the one card on the page that is a pitch. */
const GLOW: CSSProperties = {
  backgroundImage:
    "radial-gradient(42rem 20rem at 85% -6rem, color-mix(in oklab, var(--color-accent) 11%, transparent), transparent 70%)",
};

const GLOW_LOW: CSSProperties = {
  backgroundImage:
    "radial-gradient(36rem 14rem at 100% 120%, color-mix(in oklab, var(--color-accent) 8%, transparent), transparent 70%)",
};

const TOP_UPS = [10, 25, 50, 100, 250, 500].map((dollars) => dollars * 1_000_000);

/** The page translator, as the helpers below take it. */
type Translate = ReturnType<typeof useTranslation<"referrals">>["t"];

function AffiliateLink({ label, className }: { label: string; className?: string }) {
  const { i18n } = useTranslation("referrals");
  /*
   * The language travels on the link, as it does from the marketing site.
   * The cookie is shared too, but only once somebody has picked a language;
   * a reader who was merely detected as Indonesian has no cookie yet, and
   * without this the affiliate dashboard would detect them a second time.
   */
  const href = AFFILIATE_URL
    ? i18n.language === DEFAULT_LOCALE
      ? AFFILIATE_URL
      : `${AFFILIATE_URL}${AFFILIATE_URL.includes("?") ? "&" : "?"}lang=${i18n.language}`
    : undefined;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={buttonStyles("primary", "lg", cx("shrink-0", className))}
    >
      {label}
      <Icon name="arrow-up-right" className="size-3.5" />
    </a>
  );
}

function SectionHeading({ id, title }: { id: string; title: string }) {
  return (
    <h2 id={id} className="text-base font-medium tracking-[-0.01em] text-fg">
      {title}
    </h2>
  );
}

/**
 * What an affiliate actually hands out, and what it comes back as.
 *
 * Marked as an example in words, not only by styling: it is built from the
 * same parts as real UI, and a customer who read it as their own link would try
 * to copy it. The amounts come from the programme's terms, so the example
 * cannot promise more than a real commission would record.
 */
function LinkPreview({ program }: { program: AffiliateProgram | undefined }) {
  const { t } = useTranslation("referrals");
  const host = hostOf(SITE_URL);
  const topUp = 100_000_000;

  return (
    <figure
      aria-label={t("preview.label")}
      className="relative min-w-0 rounded-lg border border-line bg-canvas/80 p-3.5 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] sm:p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="micro">{t("preview.yourLink")}</span>
        <span className="rounded-sm border border-line px-1.5 py-0.5 text-[11px] text-fg-subtle">
          Example
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-2.5 sm:gap-2.5 sm:px-3">
        <Icon name="link" className="size-3.5 shrink-0 text-fg-subtle" />
        <span className="min-w-0 truncate font-mono text-[12px] text-fg-muted sm:text-[13px]">
          {host}/?ref=<span className="text-accent">{t("preview.handle")}</span>
        </span>
        <Icon name="copy" className="ml-auto size-3.5 shrink-0 text-fg-subtle" />
      </div>

      <ul className="mt-4 space-y-2">
        <PreviewEvent
          icon="team"
          title={t("preview.signUp")}
          detail={t("preview.fromLink")}
          when="2m"
        />
        {program && (
          <PreviewEvent
            icon="card"
            title={t("preview.firstTopUp", { amount: formatTermsAmount(topUp) })}
            detail={t("preview.youEarned")}
            when="1h"
            amount={`+${formatTermsAmount(commissionFor(program, topUp))}`}
          />
        )}
      </ul>
    </figure>
  );
}

function PreviewEvent({
  icon,
  title,
  detail,
  when,
  amount,
}: {
  icon: IconName;
  title: string;
  detail: string;
  when: string;
  amount?: string;
}) {
  return (
    <li className="flex items-center gap-3 rounded-md border border-line/60 bg-surface/60 px-3 py-2.5">
      <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-raised text-fg-muted">
        <Icon name={icon} className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-fg">{title}</span>
        <span className="block truncate text-[12px] text-fg-subtle">{detail}</span>
      </span>
      {amount ? (
        <span className="shrink-0 font-mono text-[13px] font-medium text-mint tabular-nums">
          {amount}
        </span>
      ) : (
        <span className="shrink-0 font-mono text-[11px] text-fg-subtle">{when}</span>
      )}
    </li>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "maxlayer.cloud";
  }
}

function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="bg-surface px-6 py-5">
      <dt className="micro">{label}</dt>
      <dd className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-2xl leading-none font-light tracking-[-0.02em] text-fg tabular-nums">
          {value}
        </span>
        <span className="text-[12px] text-fg-subtle">{note}</span>
      </dd>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  last,
  children,
}: {
  n: number;
  icon: IconName;
  title: string;
  last: boolean;
  children: ReactNode;
}) {
  return (
    <li className="card relative p-5">
      <div className="flex items-center justify-between">
        <span className="grid size-9 place-items-center rounded-md border border-accent/25 bg-accent/8 text-accent">
          <Icon name={icon} className="size-4" />
        </span>
        <span className="font-mono text-[11px] text-fg-subtle">{String(n).padStart(2, "0")}</span>
      </div>
      <h3 className="mt-4 text-sm font-medium text-fg">{title}</h3>
      <p className="mt-1.5 text-[13px] leading-5 text-fg-muted">{children}</p>
      {!last && (
        <Icon
          name="chevron"
          aria-hidden
          className="absolute top-1/2 -right-[1.125rem] hidden size-3 -translate-y-1/2 text-fg-subtle md:block"
        />
      )}
    </li>
  );
}

/**
 * The estimate: a number of customers and a typical first top-up, and what
 * that comes to with the cap applied.
 *
 * Top-ups are fixed choices rather than a free field. The point is to see how
 * the rate and the cap trade off, which six stops show, and a text box would
 * invite typing somebody's real forecast into a page that is not keeping it.
 */
function Estimate({ program }: { program: AffiliateProgram }) {
  const { t } = useTranslation("referrals");
  const [customers, setCustomers] = useState(10);
  const [topUp, setTopUp] = useState(50_000_000);

  const perCustomer = commissionFor(program, topUp);
  const total = perCustomer * customers;
  const capped = Math.floor((topUp * program.commissionBps) / 10_000) > program.commissionCapMicros;

  return (
    <section aria-labelledby="referral-estimate" className="card overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <div className="space-y-8 p-6 sm:p-8">
          <div className="space-y-1">
            <SectionHeading id="referral-estimate" title={t("estimate.title")} />
            <p className="text-[13px] text-fg-muted">{t("estimate.lead")}</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor="referral-customers" className="text-[13px] text-fg">
                {t("estimate.people")}
              </label>
              <span className="font-mono text-sm text-fg tabular-nums">{customers}</span>
            </div>
            <input
              id="referral-customers"
              type="range"
              min={1}
              max={50}
              step={1}
              value={customers}
              onChange={(event) => setCustomers(Number(event.target.value))}
              className="block h-2 w-full cursor-pointer accent-accent pointer-coarse:h-11"
            />
            <div className="flex justify-between font-mono text-[11px] text-fg-subtle">
              <span>1</span>
              <span>50</span>
            </div>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-[13px] text-fg">{t("estimate.topUp")}</legend>
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
              {TOP_UPS.map((amount) => {
                const active = amount === topUp;
                return (
                  <button
                    key={amount}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTopUp(amount)}
                    className={cx(
                      "h-9 rounded-md border font-mono text-[13px] tabular-nums transition-colors duration-200 ease-smooth pointer-coarse:h-11",
                      active
                        ? "border-accent/50 bg-accent/10 text-accent"
                        : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                    )}
                  >
                    {formatTermsAmount(amount)}
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div
          aria-live="polite"
          className="flex flex-col justify-center gap-3 border-t border-line bg-canvas/50 p-6 sm:p-8 lg:border-t-0 lg:border-l"
        >
          <p className="micro">{t("estimate.couldEarn")}</p>
          <p className="text-5xl leading-none font-light tracking-[-0.035em] text-fg tabular-nums sm:text-6xl">
            {formatTermsAmount(total)}
          </p>
          <p className="text-[13px] text-fg-muted">
            {t("estimate.each", { amount: formatTermsAmount(perCustomer), count: customers })}
          </p>
          {capped && (
            <p className="inline-flex items-center gap-1.5 text-[12px] text-accent">
              <Icon name="warning" className="size-3.5 shrink-0" />
              {t("estimate.capped", { amount: formatTermsAmount(program.commissionCapMicros) })}
            </p>
          )}
          <p className="mt-2 border-t border-line pt-4 text-[12px] leading-5 text-fg-subtle">
            {t("estimate.payout", {
              hold:
                program.holdDays > 0
                  ? t("estimate.hold", { window: t("daysCount", { count: program.holdDays }) })
                  : "",
              minimum: formatTermsAmount(program.minPayoutMicros),
            })}
          </p>
        </div>
      </div>
    </section>
  );
}

function answers(
  program: AffiliateProgram | undefined,
  t: Translate,
): { question: string; answer: string }[] {
  /* Whole sentences per branch rather than a stem with a clause glued on: the
     hold period reads before the condition in English and need not anywhere
     else, so the placeholder decides where it lands. */
  const days = (count: number) => t("daysCount", { count });

  return [
    { question: t("faq.who.question"), answer: t("faq.who.answer") },
    {
      question: t("faq.firstTopUp.question"),
      answer: program
        ? t("faq.firstTopUp.answer", {
            rate: formatCommissionRate(program.commissionBps),
            cap: formatTermsAmount(program.commissionCapMicros),
          })
        : t("faq.firstTopUp.answerNoProgram"),
    },
    {
      question: t("faq.paid.question"),
      answer: program
        ? t("faq.paid.answer", {
            hold:
              program.holdDays > 0
                ? t("faq.paid.hold", { window: days(program.holdDays) })
                : "",
            minimum: formatTermsAmount(program.minPayoutMicros),
          })
        : t("faq.paid.answerNoProgram"),
    },
    {
      question: t("faq.window.question"),
      answer: program
        ? t("faq.window.answer", { window: days(program.cookieDays) })
        : t("faq.window.answerNoProgram"),
    },
    { question: t("faq.self.question"), answer: t("faq.self.answer") },
  ];
}
